import {
  Message, MessagesContainer, MessageContainer,
  ActionButtons
} from "./styles";
import React, {useEffect, useRef, useState} from "react";
import {
  Button,
  Col,
  Collapse,
  Empty, Image,
  Popover,
  Row,
  Space,
  Spin,
  Tag,
  theme,
  Tooltip,
  Typography,
} from "antd";
import {
  DeleteOutlined,
  FileOutlined,
  InfoCircleOutlined,
  ThunderboltOutlined, WarningOutlined,
  EditOutlined,
  CopyOutlined,
  RetweetOutlined,
  MessageOutlined, DownloadOutlined,
} from "@ant-design/icons";
import AudioMessage from "./AudioMessage";
import {MessageEditor} from "./MessageEditor";
import {TriggerResponseModal} from "./TriggerResponseModal";

const MessageActions = ({
                          type,
                          isMachine,
                          content,
                          setEditingIndex,
                          messageIndex,
                          onDelete,
                          onRetry,
                          actionLoading,
                          messageId
                        }) => {
  const isTextBased = ['text', 'multiple'].includes(type);
  const [isCopied, setIsCopied] = useState(false);

  const handleCopy = async () => {
    if (!content) return;
    await navigator.clipboard.writeText(content);
    setIsCopied(true);
    setTimeout(() => {
      setIsCopied(false);
    }, 2000);
  };

  return (
    <ActionButtons>
      {isTextBased && (
        <>
          <Tooltip title={isCopied ? 'Copiado!' : 'Copiar'}>
            <Button onClick={handleCopy} type={"text"} size="small" icon={<CopyOutlined/>}/>
          </Tooltip>
          <Tooltip title="Editar">
            <Button
              loading={actionLoading?.type === 'edit' && actionLoading?.id === messageId}
              onClick={() => setEditingIndex(messageIndex)} type={"text"} size="small" icon={<EditOutlined/>}/>
          </Tooltip>
        </>
      )}
      {!isMachine && (
        <Tooltip title="Refazer">
          <Button
            loading={actionLoading?.type === 'retry' && actionLoading?.id === messageId}
            onClick={onRetry} type={"text"} size="small" icon={<RetweetOutlined/>}/>
        </Tooltip>
      )}
      <Tooltip title="Apagar">
        <Button
          loading={actionLoading?.type === 'delete' && actionLoading?.index === messageIndex}
          onClick={onDelete} type={"text"} size="small" icon={<DeleteOutlined/>} danger/>
      </Tooltip>
    </ActionButtons>
  );
};

export function Messages({waitingMessage, messages, newMessage, onDelete, onEdit, onRetry, actionLoading}) {
  const {
    token: {colorText, colorBgLayout, colorErrorBg, colorErrorText, colorPrimaryBgHover, colorTextTertiary},
  } = theme.useToken();
  const [editingIndex, setEditingIndex] = useState(null);
  const prevMessageCount = useRef(messages.length);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedActions, setSelectedActions] = useState([]);

  useEffect(() => {
    prevMessageCount.current = messages.length;
  }, [messages.length]);

  const isDeletion = messages.length < prevMessageCount.current;

  if (messages.length < 1)
    return <Empty style={{image: {height: 150, transform: 'scale(2)'}}}
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description={'Nenhuma mensagem enviada'}/>

  const getTriggerResponseColor = (actions = []) => {

    if (!actions || actions.length === 0) {
      return 'grey';
    }

    const hasSuccess = actions.some(action => action.status_code < 400);

    const hasError = actions.some(action => action.status_code >= 400);

    if (hasSuccess && hasError) {
      return 'gold';
    }

    if (hasError) {
      return 'red';
    }

    if (hasSuccess) {
      return 'green';
    }

    return 'grey';
  };

  const handleOpenTriggerModal = (actions) => {
    setSelectedActions(actions);
    setIsModalOpen(true);
  };

  return <MessagesContainer>
    {messages?.map(({data, isMachine, isError, isSystem}, messageIndex) => (
      isSystem ? (
        <div
          key={`sys-${messageIndex}`}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            margin: '12px 4px',
            opacity: 0.7,
          }}
        >
          <div style={{ flex: 1, height: 1, background: colorBgLayout }} />
          <div
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              padding: '4px 10px',
              borderRadius: 999,
              background: colorBgLayout,
              fontSize: 11,
              fontWeight: 500,
              color: colorTextTertiary,
              letterSpacing: '0.02em',
            }}
          >
            <span
              style={{
                width: 6,
                height: 6,
                borderRadius: 999,
                background: '#ad34fe',
                boxShadow: '0 0 8px rgba(173,52,254,0.6)',
              }}
            />
            {data?.response?.[0]?.data}
          </div>
          <div style={{ flex: 1, height: 1, background: colorBgLayout }} />
        </div>
      ) : (
        <>
        <React.Fragment key={messageIndex}>
          {data?.response?.map((msg, i) => {
            const shouldAnimate = !isDeletion && messageIndex >= prevMessageCount.current;
            const isCurrentlyEditing = editingIndex === messageIndex;
            return <MessageContainer notAnimated={!shouldAnimate} key={i} isMachine={isMachine}>
              <>
                {(['text', 'multiple'].includes(msg.type) && msg.data) &&
                  (!isCurrentlyEditing ?
                    <Message bgColor={isMachine ? colorBgLayout : colorPrimaryBgHover}
                             bgColorError={colorErrorBg}
                             colorErrorText={colorErrorText}
                             isError={isError}
                             color={colorText}
                             isMachine={isMachine}>
                      {msg?.data}
                    </Message> :
                    <MessageEditor
                      message={msg}
                      onCancel={() => setEditingIndex(null)}
                      onSave={(messageId, newText) => {
                        setEditingIndex(null)
                        onEdit(messageId, newText)
                      }}/>)
                }
                {(['list'].includes(msg.type) && msg.data) && <Row gutter={[8, 8]}>
                  <Message bgColor={isMachine ? colorBgLayout : colorPrimaryBgHover}
                           bgColorError={colorErrorBg}
                           colorErrorText={colorErrorText}
                           isError={isError}
                           color={colorText}
                           isMachine={isMachine}>
                    <b>{msg?.data?.header}</b>
                    <p>{msg?.data?.body}</p>
                    <p>{msg?.data?.footer}</p>
                  </Message>
                  {msg?.data?.options?.map(({text}, i) => <Col key={i}><Button
                    onClick={() => newMessage(text)}>
                    {text}
                  </Button></Col>)}
                </Row>}
                {msg.type === 'think' && msg.data && (
                  <Collapse
                    bordered={false}
                    expandIconPosition="end"
                    style={{background: 'transparent', width: '100%'}}
                  >
                    <Collapse.Panel
                      key="1"
                      style={{
                        background: colorBgLayout,
                        borderRadius: '10px',
                        border: 'none',
                        opacity: 0.8
                      }}
                      header={
                        <Space>
                          <MessageOutlined style={{color: colorTextTertiary}}/>
                          <Typography.Text style={{color: colorTextTertiary}}>
                            Pensamento
                          </Typography.Text>
                        </Space>
                      }
                    >
                      <Typography.Text style={{color: colorTextTertiary}} italic>
                        {msg.data}
                      </Typography.Text>
                    </Collapse.Panel>
                  </Collapse>
                )}
                {(msg.type === 'options' || msg.type === 'button') && <Row gutter={[8, 8]}>
                  {msg.data.map(({text, fileList}, i) => <Col key={i}><Button
                    onClick={() => newMessage(text, fileList)}>
                    {text}
                  </Button></Col>)}
                </Row>}
                {(msg.type === 'options' || msg.type === 'button') && <Row gutter={[8, 8]}>
                  {msg.data.map(({text, fileList}, i) => <Col key={i}><Button
                    onClick={() => newMessage(text, fileList)}>
                    {text}
                  </Button></Col>)}
                </Row>}

                {msg.type === 'file' &&
                  <Space>
                    <a href={msg?.data?.url} target="_blank">
                      <Button type="primary" icon={<DownloadOutlined/>}>
                        <Typography.Text style={{width: 200, color: 'inherit', fontSize: 'inherit'}} ellipsis>{msg?.data?.caption || msg?.data?.url}</Typography.Text>
                      </Button>
                    </a>
                  </Space>
                }

                {msg.type === "audio" &&
                  <AudioMessage src={msg.url || msg.data} duration={msg.audioDuration}
                                bgColor={colorPrimaryBgHover} isMachine={isMachine}/>
                }

                {(msg.type === 'image' || msg.type === 'imageV2') &&
                  <Message bgColor={isMachine ? colorBgLayout : colorPrimaryBgHover}
                           bgColorError={colorErrorBg}
                           colorErrorText={colorErrorText}
                           isError={isError}
                           color={colorText}
                           isMachine={isMachine}
                           style={{padding: '0.3rem'}}
                  >
                    <Image style={{borderRadius: 12}} src={msg.type === 'imageV2' ? msg.data.url : msg.data}/>
                    {msg.type === 'imageV2' && msg.data.caption &&
                      <div style={{padding: '0 .5rem .5rem .5rem'}}>{msg.data.caption}</div>}
                  </Message>
                }
              </>

              {['text', 'multiple', 'file', 'audio', 'image', 'imageV2'].includes(msg.type) && !isCurrentlyEditing
                &&
                <MessageActions
                  type={msg.type}
                  isMachine={isMachine}
                  content={msg.data}
                  messageId={msg.id}
                  setEditingIndex={setEditingIndex}
                  messageIndex={messageIndex}
                  onDelete={() => onDelete(messageIndex)}
                  onRetry={() => onRetry(msg.id)}
                  actionLoading={actionLoading}
                />
              }

              {msg.type === 'trigger' &&
                <>
                  <Button
                    onClick={() => handleOpenTriggerModal(msg.data.actions)}
                    icon={<ThunderboltOutlined/>}
                    variant="filled"
                    color={getTriggerResponseColor(msg.data.actions)}
                    size="small"
                    style={{fontSize: 12, whiteSpace: 'normal', wordBreak: 'break-word'}}>
                    {msg?.data?.function_name}({Object.values(msg?.data?.args ?? {})?.join(", ")})
                  </Button>
                </>
              }
              {msg.type === 'reference' && msg?.data?.length > 0 &&
                <Space direction={'horizontal'} style={{textAlign: 'right', marginRight: 4,}}>
                  {msg.data.map((item, i) => <Popover key={i} placement={"top"}
                                                      content={<p>{item.content}</p>}
                                                      title={item.title}>
                    <Button size={'small'} icon={<InfoCircleOutlined/>}/>
                  </Popover>)}
                </Space>}
            </MessageContainer>
          })
          }
        </React.Fragment>
        {data?.isAnythingElse &&
          <Space>
            <Tag icon={<WarningOutlined/>} color={'warning'}>Fora do contexto</Tag>
          </Space>
        }
        {isMachine &&
          <Row style={{marginTop: 8}} gutter={[8, 8]}>
            {data?.intents?.filter(item => item.confidence > .7)?.map((item, i) => <Col><Tag
              key={i}>
              #{item.name} {parseInt(item.confidence * 100)}%
            </Tag></Col>)}
            {data?.entities?.filter(item => item.confidence > .7)?.map((item, i) => <Col><Tag
              key={i}>
              {item?.entity[0] === '@' ? `${item.entity}(${item?.matched})` : '@' + item.entity} {parseInt(item.confidence * 100)}%
            </Tag></Col>)}
          </Row>}
        </>
      )
    ))}

    {waitingMessage && (
      <MessageContainer isMachine={true}>
        <Message bgColor={colorBgLayout} isMachine={true}>
          <Space>
            <Spin size="small"/>
            <Typography.Text style={{color: colorTextTertiary}}>
              Boo esta pensando...
            </Typography.Text>
          </Space>
        </Message>
      </MessageContainer>
    )}

    <TriggerResponseModal
      isOpen={isModalOpen}
      setIsOpen={setIsModalOpen}
      actions={selectedActions}
    />
  </MessagesContainer>;
}
